'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { signOut } from 'firebase/auth';
import { isCommunioShellBuild } from '@/config/brand';
import { marketplaceNav } from '@/config/marketplaceNav';
import { useMarketplaceNavVisible } from '@/platform/navTour/useMarketplaceNavVisible';
import { useMarketplaceSellLabel } from '@/platform/marketplace/useMarketplaceSellLabel';
import { accountMenuLinks, isOpsItsmBuild } from './accountMenuLinks';
import { getFirebaseAuth } from '@/platform/lib/firebase';
import { clearOpsAuthSession } from '@/platform/lib/idTokenStore';
import { resolvedApiBaseUrl } from '@/platform/lib/apiBase';
import { ProductNavLink } from '@/platform/lib/ProductNavLink';
import { ThemeSettingsModal } from '@/platform/theme/ThemeSettings';
import { avatarInitials } from './avatar';
import { useAuth } from './useAuth';
import { useAdmin } from '@/platform/admin/useAdmin';
import { displayHandle } from '@/platform/users/displayHandle';
import { useIsCommunityHost } from './useIsCommunityHost';

const showCommunityGrowth = isCommunioShellBuild() && !isOpsItsmBuild();

export function AccountMenu() {
  const ops = isOpsItsmBuild();
  const { user, profile } = useAuth();
  const { isAdmin } = useAdmin();
  const { ready: hostReady, isHost } = useIsCommunityHost(showCommunityGrowth);
  const { ready: marketplaceNavReady, visible: marketplaceNavVisible } = useMarketplaceNavVisible();
  const sellLabel = useMarketplaceSellLabel();
  const menuLinks = accountMenuLinks(ops);
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  if (!user) return null;

  const fullName = profile?.displayName ?? user.displayName ?? 'Member';
  const username = displayHandle(profile?.handle, user.uid);

  async function logout() {
    setOpen(false);
    try {
      clearOpsAuthSession();
      try {
        await signOut(getFirebaseAuth());
      } catch {
        /* product slug may have no local Firebase session */
      }
      await fetch(`${resolvedApiBaseUrl()}/auth/session`, { method: 'DELETE', credentials: 'include' });
    } catch {
      /* ignore */
    }
    // Hard navigate so ops-bounce React state cannot stick after session clear.
    if (typeof window !== 'undefined') {
      window.location.assign('/');
      return;
    }
    router.push('/');
    router.refresh();
  }

  return (
    <>
      <div className="account-menu" ref={rootRef}>
        <button
          type="button"
          className="account-menu__trigger"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label="Account menu"
        >
          <span className="profile-photo">
            {user.photoURL ? (
              <img src={user.photoURL} alt="" />
            ) : (
              avatarInitials(user.displayName, user.email)
            )}
          </span>
        </button>
        {open && (
          <div className="account-menu__panel" role="menu">
            <div className="account-menu__header">
              <p className="account-menu__name">{fullName}</p>
              <p className="account-menu__handle text-muted">@{username}</p>
            </div>
            <p className="account-menu__section">Account</p>
            {!ops ? (
              <ProductNavLink
                href={`/users/${user.uid}/`}
                className="account-menu__item"
                role="menuitem"
                onClick={() => setOpen(false)}
              >
                Profile
              </ProductNavLink>
            ) : null}
            {!ops ? (
              <ProductNavLink
                href="/blog/mine/"
                className="account-menu__item"
                role="menuitem"
                onClick={() => setOpen(false)}
              >
                My blog posts
              </ProductNavLink>
            ) : null}
            {menuLinks.map((item) => (
              <ProductNavLink
                key={item.href}
                href={item.href}
                className="account-menu__item"
                role="menuitem"
                onClick={() => setOpen(false)}
              >
                {item.label}
              </ProductNavLink>
            ))}
            {showCommunityGrowth && hostReady && isHost ? (
              <ProductNavLink
                href="/help/"
                className="account-menu__item"
                role="menuitem"
                data-testid="account-menu-host-help"
                onClick={() => setOpen(false)}
              >
                Help
              </ProductNavLink>
            ) : null}
            {showCommunityGrowth && hostReady && isHost ? (
              <ProductNavLink
                href="/saas/portal/"
                className="account-menu__item"
                role="menuitem"
                data-testid="account-menu-community-host"
                onClick={() => setOpen(false)}
              >
                Community & marketplace
              </ProductNavLink>
            ) : null}
            {!ops && marketplaceNavReady && marketplaceNavVisible && (
              <>
                <ProductNavLink
                  href={marketplaceNav.orders.href}
                  className="account-menu__item"
                  role="menuitem"
                  onClick={() => setOpen(false)}
                >
                  {marketplaceNav.orders.label}
                </ProductNavLink>
                <ProductNavLink
                  href={marketplaceNav.sell.href}
                  className="account-menu__item"
                  role="menuitem"
                  onClick={() => setOpen(false)}
                >
                  {sellLabel}
                </ProductNavLink>
              </>
            )}
            {!ops && isAdmin && (
              <ProductNavLink
                href="/admin/"
                className="account-menu__item"
                role="menuitem"
                onClick={() => setOpen(false)}
              >
                Admin
              </ProductNavLink>
            )}
            <button
              type="button"
              className="account-menu__item"
              role="menuitem"
              data-testid="account-menu-theme"
              onClick={() => {
                setOpen(false);
                setThemeOpen(true);
              }}
            >
              Theme
            </button>
            <button type="button" className="account-menu__item account-menu__sign-out" role="menuitem" onClick={() => void logout()}>
              Sign out
            </button>
          </div>
        )}
      </div>
      <ThemeSettingsModal open={themeOpen} onClose={() => setThemeOpen(false)} />
    </>
  );
}
